import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _orderBy from 'lodash/orderBy';

// Components
import SidebarTitle from './SidebarTitle/SidebarTitle';
import SidebarMapAdmins from './SidebarMapAdmins/SidebarMapAdmins';
import SidebarMapFiltersField from './SidebarMapFiltersType/SidebarMapFiltersField';
import SidebarMapFiltersBreaks from './SidebarMapFiltersBreaks/SidebarMapFiltersBreaks';
import SidebarMapLegend from './SidebarMapLegend/SidebarMapLegend';
import SidebarMapFiltersDivider from './SidebarMapFiltersDivider/SidebarMapFiltersDivider';
import Admin2MapNote from '../Admin2MapNote/Admin2MapNote';

// Controllers
import mapController from '../../controllers/MapControllers';

// Store
import {
  $adminSelected,
  $colorRangeIndexSelected,
  $colorRangeSteps,
  $fieldSelected,
  $rendererClassificationMethod,
} from '../../store/slices/sidebarMapFiltersSlice';
import {
  $indicatorSelected,
  $indicatorsObject,
  $levelSelected,
  $surveySelected,
  LEVEL_ALL,
  setAllSurveys,
  setSurveyList,
  setSurveySelected,
} from '../../store/slices/admin2EstimatesSlice';
import { $mapViewReady } from '../../store/slices/mapSlice';

// Services
import { getMapServerAdmin2Data } from '../../services/getMapServerAdmin2Data.service';
import { getAdmin2FeaturesData, fetchAdmin2FeaturesWithGeometries } from '../../services/getGeometries.service';
import {
  getAllSurveysRasterServicesData,
  AllSurveysRasterServicesObjectType,
} from '../../services/getSurveyRasterData.service';

// Utils
import { getFeatureLayerID, reorderAdminBoundariesLayerToTop } from '../../utils/map.utils';
import { handleAdmin1ClientSideFeaturesLayerUpdates } from '../../utils/admin1Map.utils';
import { updatePopupTemplateForFieldSelected } from '../../utils/popup.utils';
import { getSurveyDropdownLabelValue, getSurveysListUniq } from '../../utils/survey.utils';

// Configs
import { ADMIN1_REGION_ID_FIELD, ADMIN__5X5km, ADMIN__Admin1, ADMIN__Admin2 } from '../../configs/map.configs';
import {
  ATTRIBUTE_FIELD_ADMIN_0_NAME,
  ATTRIBUTE_FIELD_ADMIN_1_NAME,
  ATTRIBUTE_FIELD_ADMIN_2_NAME,
  ATTRIBUTE_FIELD_COUNTRY_CODE,
  ATTRIBUTE_FIELD_FOR_ESTIMATES,
  ATTRIBUTE_FIELD_FOR_POPULATION,
  ATTRIBUTE_FILED_FOR_UNCERTAINTY,
} from '../../configs/attributeFields.configs';
import { DOWNLOAD_PACKAGE_FROM_S3 } from '../../configs/s3.configs';

// Styles
import './Admin2Map.scss';

function Admin2Map() {
  const dispatch = useDispatch();
  const mapViewRef = useRef(null);
  const mapLegendRef = useRef(null);
  const surveySelected = useSelector($surveySelected);
  const levelSelected = useSelector($levelSelected);
  const indicatorsObject = useSelector($indicatorsObject);
  const indicatorSelected = useSelector($indicatorSelected);

  const mapViewReady = useSelector($mapViewReady);
  const fieldSelected = useSelector($fieldSelected);
  const adminSelected = useSelector($adminSelected);
  const colorRangeSteps = useSelector($colorRangeSteps);
  const colorRangeIndexSelected = useSelector($colorRangeIndexSelected);
  const rendererClassificationMethod = useSelector($rendererClassificationMethod);

  useEffect(() => {
    mapController.initializeMap(mapViewRef, mapLegendRef);
  }, [mapViewRef, mapLegendRef]);

  useEffect(() => {
    mapController.updateAdminRenderer(mapLegendRef, adminSelected);
  }, [rendererClassificationMethod, colorRangeSteps, colorRangeIndexSelected]);

  useEffect(() => {
    getMapServerAdmin2Data().then((features: any) => {
      const surveyList: any = [];

      const featuresFilteredList = features?.filter((feature: any) => {
        const { SurveyType = 'DHS', year } = feature?.attributes;
        const admin0 = feature?.attributes[ATTRIBUTE_FIELD_ADMIN_0_NAME];
        const admin1 = feature?.attributes[ATTRIBUTE_FIELD_ADMIN_1_NAME];
        const admin2 = feature?.attributes[ATTRIBUTE_FIELD_ADMIN_2_NAME];
        const dhsCountryCode = feature?.attributes[ATTRIBUTE_FIELD_COUNTRY_CODE];

        feature.attributes['DHSCC'] = dhsCountryCode;
        feature.attributes['SurveyType'] = SurveyType;
        feature.attributes[ATTRIBUTE_FIELD_FOR_ESTIMATES] = feature?.attributes['value'] || -1;
        feature.attributes[ATTRIBUTE_FIELD_FOR_POPULATION] = feature?.attributes['pop_estimate'] || -1;
        feature.attributes[ATTRIBUTE_FILED_FOR_UNCERTAINTY] = feature?.attributes['width_ci'] || -1;

        if (admin0 && admin1 && admin2) {
          const surveyDropdownLabelValue = getSurveyDropdownLabelValue(admin0, year, SurveyType);

          const surveyItem = {
            ADM0: admin0,
            DHSCC: dhsCountryCode,
            SurveyType,
            year,
            value: surveyDropdownLabelValue,
          };

          surveyList.push(surveyItem);
          return admin0 && admin1 && admin2;
        }
      });

      dispatch(setAllSurveys(featuresFilteredList || []));
      const surveysListUniq = getSurveysListUniq(surveyList);

      getAllSurveysRasterServicesData().then((allSurveysRasterServicesObject: AllSurveysRasterServicesObjectType) => {
        const surveysListUniqWithRasterServices = surveysListUniq
          .map((survey: any) => {
            const countryCode = survey['DHSCC'];
            const surveyType = survey['SurveyType'];
            const surveyYear = survey['year'];
            const surveyRasterService = allSurveysRasterServicesObject[countryCode];

            const newSurvey = {
              ...survey,
              rasterServiceURL: surveyRasterService?.url,
              downloadPackageFromS3Link: `${DOWNLOAD_PACKAGE_FROM_S3}${countryCode}${surveyType}${surveyYear}_adm2.zip`,
            };

            return newSurvey;
          })
          .filter((survey: any) => {
            return Boolean(survey.rasterServiceURL);
          });

        const surveysListUniqWithRasterServicesOrdered = _orderBy(surveysListUniqWithRasterServices, ['DHSCC']);
        dispatch(setSurveyList(surveysListUniqWithRasterServicesOrdered));
        dispatch(setSurveySelected(surveysListUniqWithRasterServicesOrdered[0]));
      });
    });
  }, []);

  useEffect(() => {
    if (levelSelected) {
      mapController.handleAdmin1Update(adminSelected, levelSelected);

      if (mapController?.admin2ClientSideFeaturesLayer && adminSelected === ADMIN__Admin2) {
        const countryCode = surveySelected?.DHSCC;
        const SurveyType = surveySelected?.SurveyType;

        if (countryCode && SurveyType) {
          getAdmin2FeaturesData(countryCode, SurveyType).then((admin2FeaturesData) => {
            const indicatorID = indicatorSelected?.value;
            mapController.updateAdmin2Graphic(admin2FeaturesData, indicatorID, surveySelected).then(() => {
              reorderAdminBoundariesLayerToTop();
            });
          });
        }
      }
    }
  }, [levelSelected, adminSelected]);

  useEffect(() => {
    mapController.handlePopupMarkerGraphicsLayer(adminSelected);
    mapController.handleAdmin1ClientSideFeaturesLayerVisibility(adminSelected);
    mapController.handleAdmin2ClientSideFeaturesLayerVisibility(adminSelected);
    mapController.handleCountryMapImageLayerVisibility(adminSelected, indicatorSelected, surveySelected, fieldSelected);
    mapController.handleAdmin2ReferenceLayerVisibility(adminSelected);
  }, [adminSelected]);

  useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      console.log('indicatorSelected', indicatorSelected);
    }

    if (adminSelected === ADMIN__Admin1) {
      handleAdmin1ClientSideFeaturesLayerUpdates(surveySelected, indicatorsObject, indicatorSelected, adminSelected);
    }

    if (adminSelected === ADMIN__Admin2) {
      const countryCode = surveySelected?.DHSCC;
      const SurveyType = surveySelected?.SurveyType;
      const countryName = surveySelected?.ADM0;
      const indicatorID = indicatorSelected?.value;

      if (countryCode && SurveyType) {
        getAdmin2FeaturesData(countryCode, SurveyType).then((admin2FeaturesData) => {
          if (import.meta.env.MODE === 'development') {
            console.log('admin2FeaturesData:', admin2FeaturesData);
          }

          const admin2ClientSideFeaturesLayerID = getFeatureLayerID();
          if (mapController?.admin2ClientSideFeaturesLayer?.id !== admin2ClientSideFeaturesLayerID && countryName) {
            fetchAdmin2FeaturesWithGeometries(countryName).then((admin2FeaturesWithGeometries) => {
              if (import.meta.env.MODE === 'development') {
                console.log(admin2FeaturesWithGeometries);
              }

              mapController
                .setAdmin2Graphic(admin2FeaturesData, admin2FeaturesWithGeometries, indicatorID, surveySelected)
                .then(() => {
                  mapController.updateAdmin2Graphic(admin2FeaturesData, indicatorID, surveySelected).then(() => {
                    reorderAdminBoundariesLayerToTop();
                  });
                });
            });
          } else {
            if (import.meta.env.MODE === 'development') {
              console.log('Update admin level 2');
            }

            mapController.updateAdmin2Graphic(admin2FeaturesData, indicatorID, surveySelected, true).then(() => {
              reorderAdminBoundariesLayerToTop();
            });
          }
        });
      }
    }

    if (adminSelected === ADMIN__5X5km) {
      const countryName = surveySelected?.ADM0;
      mapController.handleCountryMapImageLayerVisibility(
        adminSelected,
        indicatorSelected,
        surveySelected,
        fieldSelected
      );

      // Add admin 2 reference layer if needed or adjust visiblity
      if (countryName && !mapController.admin2ReferenceFeatureLayer) {
        mapController.addAdmin2ReferenceFeatureLayer(countryName);
      } else {
        mapController.handleAdmin2ReferenceLayerVisibility(adminSelected);
      }
    }
  }, [surveySelected, indicatorSelected, adminSelected, fieldSelected]);

  // Close popup when selections are changed to avoid it lingering with old data
  useEffect(() => {
    if (mapController?.mapView?.popup.visible) {
      mapController?.mapView?.popup.close();
    }
  }, [surveySelected, levelSelected, indicatorSelected, adminSelected, fieldSelected]);

  // Update popup when field is changed to update the popup content
  useEffect(() => {
    const updateLayers = [mapController.admin1ClientSideFeaturesLayer, mapController.admin2ClientSideFeaturesLayer];

    updateLayers.forEach((layer) => {
      if (layer) {
        updatePopupTemplateForFieldSelected(layer);
      }
    });
  }, [fieldSelected]);

  useEffect(() => {
    if (adminSelected === ADMIN__Admin2 || adminSelected === ADMIN__5X5km) {
      handleAdmin1ClientSideFeaturesLayerUpdates(surveySelected, indicatorsObject, indicatorSelected, adminSelected);
    }
  }, [surveySelected?.value]); // Value example - "Ethiopia DHS 2016"

  return (
    <section className='row admin-2-estimates__body'>
      <aside className='Sidebar__container col-lg-4 col-md-4'>
        <div className='admin-2-estimates__sidebar-map'>
          <SidebarTitle title={'MAP'} />
        </div>

        <SidebarMapAdmins />

        <SidebarMapFiltersDivider />

        <SidebarMapFiltersField />

        <SidebarMapFiltersDivider />

        <SidebarMapFiltersBreaks />

        <SidebarMapLegend mapLegendRef={mapLegendRef} />
      </aside>

      <div className='col-lg-8 col-md-8'>
        <div ref={mapViewRef} className='mapContainer'>
          <Admin2MapNote />
        </div>
      </div>
    </section>
  );
}

export default Admin2Map;
